import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Download, 
  Filter, 
  Search, 
  Eye, 
  ChevronDown,
  ChevronUp,
  RefreshCw,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  BanknoteIcon,
  User,
  Building,
  X,
  Copy,
  ExternalLink,
  Info
} from 'lucide-react';
import { getTransferHistory, TransferHistoryItem, TransferHistoryFilters, getTransferDetails, exportTransferHistory } from '../../services/transferService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../LoadingScreen';

interface TransferHistoryProps {
  // When true, lock the type filter to 'wallet_to_bank' and surface the page
  // as "Bank Transfers" — this is the History sub-item under the Transfers
  // sidebar group, which users expect to show only bank transfers.
  onlyBankTransfers?: boolean;
}

const TransferHistory: React.FC<TransferHistoryProps> = ({ onlyBankTransfers = false }) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    operator: 'all',
    currency: 'all',
    status: 'all',
    type: onlyBankTransfers ? 'wallet_to_bank' : 'all',
    method: 'all'
  });



  const [searchTerm, setSearchTerm] = useState('');
  const [transfers, setTransfers] = useState<TransferHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransfers, setTotalTransfers] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ 
    key: 'created_at', 
    direction: 'desc' 
  });
  const [selectedTransfer, setSelectedTransfer] = useState<TransferHistoryItem | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Options de filtre
  const operators = ['orange', 'airtel', 'afrimoney', 'mpesa'];
  const currencies = ['CDF', 'USD'];
  const statuses = ['SUCCESS', 'PENDING', 'FAILED', 'APPROVED', 'REJECTED', 'PROCESSING'];
  const types = ['wallet_to_wallet', 'wallet_to_bank', 'bank_to_wallet', 'inter_operator'];
  const methods = ['mobile_money', 'bank_transfer', 'cash', 'card', 'wire_transfer'];

  const [apiResponse, setApiResponse] = useState<any>(null);

  const [showExportMenu, setShowExportMenu] = useState(false);

  // Fonction d'export de la page courante (comportement actuel)
  const handleExportCurrentPage = () => {
    const headers = [
      'ID', 'Date', 'Type', 'Method', 'Operator', 
      'From', 'To', 'Amount', 'Currency', 
      'Status', 'Reference', 'Initiated By', 'Processed By', 'Comments'
    ];
    
    const csvContent = transfers.map(transfer => {
      const date = transfer.created_at || transfer.created_at || '';
      const typeConfig = getTypeConfig(transfer.transfer_type || '');
      const operatorConfig = getOperatorConfig(transfer.source_method || transfer.target_method || '');
      const methodConfig = getMethodConfig(transfer.method || '');
      const status = transfer.status || '';
      
      return [
        transfer.id || '',
        new Date(date).toLocaleString(),
        typeConfig.label,
        methodConfig.label,
        operatorConfig.label,
        transfer.source_method || '',
        transfer.target_method || '',
        transfer.amount?.toString() || '0',
        transfer.currency || '',
        status,
        transfer.reference || '',
        transfer.initiated_by || '',
        transfer.approved_by || '',
        transfer.rejected_reason || ''
      ].join(',');
    }).join('\n');
    
    const header = headers.join(',') + '\n';
    const blob = new Blob(['\uFEFF' + header + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transfer_history_page_${currentPage}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    setShowExportMenu(false);
  };

  const loadTransferHistory = async (page: number = 1) => {
    if (!user?.merchant_code) {
      console.warn('Merchant code not available');
      return;
    }

    setLoading(true);
    try {
      const filtersData: TransferHistoryFilters = {
        ...filters,
        merchant_code: user.merchant_code,
        search: searchTerm || undefined,
        page,
        limit: 10,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction
      };

      const response = await getTransferHistory(filtersData);
      
      // Sauvegarder la réponse complète
      setApiResponse(response);
      
      const transferData = response.data || [];
      setTransfers(transferData);
      
      const totalRecords = response.total || 0;
      setTotalPages(response.totalPages || Math.ceil(totalRecords / 10));
      setTotalTransfers(totalRecords);
      
      // On ne set plus totalAmount car il n'existe plus
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Error loading transfer history:', error);
      setApiResponse(null);
      setTransfers([]);
      setTotalTransfers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Charger les détails d'un transfert
  const loadTransferDetails = async (reference: string) => {
    if (!reference) return;
    
    setLoadingDetails(true);
    try {
      const details = await getTransferDetails(reference);
      setSelectedTransfer(details);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error loading transfer details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Effets
  useEffect(() => {
    if (user?.merchant_code) {
      loadTransferHistory();
    }
  }, [user?.merchant_code]);

  useEffect(() => {
    if (user?.merchant_code) {
      const debounceTimer = setTimeout(() => {
        loadTransferHistory(1);
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [filters, searchTerm, sortConfig, user?.merchant_code]);

  // Fonctions utilitaires
  const getStatusColor = (status: string | null) => {
    const statusUpper = status?.toUpperCase() || '';
    switch (statusUpper) {
      case 'SUCCESS':
      case 'APPROVED':
        return {
          bg: 'bg-emerald-50',
          text: 'text-emerald-700',
          border: 'border-emerald-200',
          icon: <CheckCircle className="h-4 w-4" />
        };
      case 'PENDING':
      case 'PROCESSING':
        return {
          bg: 'bg-amber-50',
          text: 'text-amber-700',
          border: 'border-amber-200',
          icon: <Clock className="h-4 w-4" />
        };
      case 'FAILED':
      case 'REJECTED':
        return {
          bg: 'bg-red-50',
          text: 'text-red-700',
          border: 'border-red-200',
          icon: <XCircle className="h-4 w-4" />
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-700',
          border: 'border-gray-200',
          icon: <AlertCircle className="h-4 w-4" />
        };
    }
  };

  const getTypeConfig = (type: string) => {
    const configs: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      'wallet_to_wallet': {
        label: 'Wallet → Wallet',
        color: 'bg-purple-100 text-purple-800',
        icon: <BanknoteIcon className="h-4 w-4" />
      },
      'wallet_to_bank': {
        label: 'Wallet → Bank',
        color: 'bg-blue-100 text-blue-800',
        icon: <Building className="h-4 w-4" />
      },
      'bank_to_wallet': {
        label: 'Bank → Wallet',
        color: 'bg-cyan-100 text-cyan-800',
        icon: <BanknoteIcon className="h-4 w-4" />
      },
      'bank_to_bank': {
        label: 'Bank → Bank',
        color: 'bg-indigo-100 text-indigo-800',
        icon: <Building className="h-4 w-4" />
      },
      'inter_operator': {
        label: 'Inter-Operator',
        color: 'bg-orange-100 text-orange-800',
        icon: <RefreshCw className="h-4 w-4" />
      }
    };
    return configs[type] || { label: type || 'Unknown', color: 'bg-gray-100 text-gray-800', icon: <FileText className="h-4 w-4" /> };
  };

  const getOperatorConfig = (operator: string | null) => {
    const operatorLower = operator?.toLowerCase() || '';
    const configs: Record<string, { label: string; color: string; bgColor: string }> = {
      'orange': { label: 'Orange Money', color: '#FF8C00', bgColor: 'bg-orange-500' },
      'airtel': { label: 'Airtel Money', color: '#E53E3E', bgColor: 'bg-red-500' },
      'afrimoney': { label: 'Afrimoney', color: '#000065', bgColor: 'bg-indigo-600' },
      'mpesa': { label: 'M-Pesa', color: '#00A651', bgColor: 'bg-green-600' }
    };
    return configs[operatorLower] || { label: operator || 'Unknown', color: '#6B7280', bgColor: 'bg-gray-500' };
  };

  const getMethodConfig = (method: string | null) => {
    const methodLower = method?.toLowerCase() || '';
    const configs: Record<string, { label: string; icon: React.ReactNode }> = {
      'mobile_money': { label: 'Mobile Money', icon: '📱' },
      'bank_transfer': { label: 'Bank Transfer', icon: '🏦' },
      'cash': { label: 'Cash', icon: '💵' },
      'card': { label: 'Card', icon: '💳' },
      'wire_transfer': { label: 'Wire Transfer', icon: '↗️' }
    };
    return configs[methodLower] || { label: method || 'Unknown', icon: '🔧' };
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleExportExcel = async () => {
    if (!user?.merchant_code) {
      console.warn('Merchant code not available');
      alert('Please login to export data');
      return;
    }

    try {
      // Montrer un indicateur de chargement
      setLoading(true);
      
      
      // Créer les filtres d'export (sans pagination pour tout exporter)
      const exportFilters: TransferHistoryFilters = {
        merchant_code: user.merchant_code,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        operator: filters.operator !== 'all' ? filters.operator : undefined,
        currency: filters.currency !== 'all' ? filters.currency : undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        type: filters.type !== 'all' ? filters.type : undefined,
        method: filters.method !== 'all' ? filters.method : undefined,
        search: searchTerm || undefined,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction
      };

      console.log('Export filters:', exportFilters);

      // Appeler l'API d'export
      const blob = await exportTransferHistory(exportFilters);
      
      // Vérifier si le blob est valide
      if (!blob || blob.size === 0) {
        throw new Error('Empty response from server');
      }
      
      // Créer et télécharger le fichier
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Créer un nom de fichier significatif
      const timestamp = new Date().toISOString().split('T')[0];
      const merchantStr = user.merchant_code || 'unknown';
      let filename = `transfer_history_${merchantStr}_${timestamp}.csv`;
      
      // Ajouter des infos de filtre au nom de fichier si nécessaire
      if (filters.dateFrom || filters.dateTo) {
        const fromDate = filters.dateFrom || 'all';
        const toDate = filters.dateTo || 'all';
        filename = `transfer_history_${merchantStr}_${fromDate}_to_${toDate}.csv`;
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      // Afficher un message de succès
      console.log('✅ Export completed successfully');
      
      // Optionnel: Afficher une notification
      alert(`Export completed! File saved as: ${filename}`);
      
    } catch (error: any) {
      console.error('❌ Error exporting transfer history:', error);
      
      // Afficher une notification d'erreur plus détaillée
      const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Error exporting transfer history. Please try again.';
      
      alert(`Export failed: ${errorMessage}`);
      
      // Log pour débogage
      if (error.response) {
        console.error('Server response:', error.response);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      operator: 'all',
      currency: 'all',
      status: 'all',
      // Keep the type lock when in bank-transfers-only mode so Reset doesn't
      // leak non-bank transfers into the view.
      type: onlyBankTransfers ? 'wallet_to_bank' : 'all',
      method: 'all'
    });
    setSearchTerm('');
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (date.toDateString() === yesterday.toDateString()) {
        return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch {
      return 'Invalid date';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatStatus = (status: string | null): string => {
    if (!status) return 'Unknown';
    const firstChar = status.charAt(0).toUpperCase();
    const restOfString = status.slice(1).toLowerCase();
    return firstChar + restOfString;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text);
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />

        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 mb-3">
              <FileText className="w-3 h-3 text-emerald-400" />
              <span className="text-[11px] font-semibold text-emerald-300 tracking-wider uppercase">
                {onlyBankTransfers ? 'Bank Transfer' : 'Transfer History'}
              </span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight">
              {onlyBankTransfers ? 'Bank Transfer' : 'Transfer History'}
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {onlyBankTransfers
                ? 'Wallet-to-Bank movements — review all your bank transfers in one place'
                : 'Monitor and manage all your financial transactions in real-time'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {user?.merchant_code && (
              <div className="flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl px-3.5 py-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-slate-300">
                  Merchant: <span className="text-white">{user.merchant_code}</span>
                </span>
              </div>
            )}

            <button
              onClick={handleExportExcel}
              disabled={!user?.merchant_code || loading || transfers.length === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all duration-200 shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              <span className="font-medium text-sm">
                {loading ? 'Exporting...' : 'Export All'}
              </span>
            </button>
          </div>
        </div>
      </div>

{/* Filtres avancés */}
{showFilters && (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
    {/* Search + reset row */}
    <div className="flex items-center gap-3 mb-3">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <input
          type="text"
          placeholder="Search by reference, account, amount, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
        />
      </div>
      <button
        onClick={resetFilters}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors whitespace-nowrap"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Reset
      </button>
    </div>

    {/* 7 inputs in a single grid row */}
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
      <div>
        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">From</label>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-xs"
        />
      </div>

      <div>
        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">To</label>
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-xs"
        />
      </div>

      {!onlyBankTransfers && (
      <div>
        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Type</label>
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-xs bg-white"
        >
          <option value="all">All Types</option>
          {types.map(type => (
            <option key={type} value={type}>{getTypeConfig(type).label}</option>
          ))}
        </select>
      </div>
      )}

      <div>
        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Status</label>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-xs bg-white"
        >
          <option value="all">All Statuses</option>
          {statuses.map(status => (
            <option key={status} value={status}>{formatStatus(status)}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Currency</label>
        <select
          value={filters.currency}
          onChange={(e) => setFilters({ ...filters, currency: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-xs bg-white"
        >
          <option value="all">All</option>
          {currencies.map(currency => (
            <option key={currency} value={currency}>{currency}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Method</label>
        <select
          value={filters.method}
          onChange={(e) => setFilters({ ...filters, method: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-xs bg-white"
        >
          <option value="all">All</option>
          {methods.map(method => (
            <option key={method} value={method}>{getMethodConfig(method).label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Operator</label>
        <select
          value={filters.operator}
          onChange={(e) => setFilters({ ...filters, operator: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-xs bg-white"
        >
          <option value="all">All</option>
          {operators.map(operator => (
            <option key={operator} value={operator}>{getOperatorConfig(operator).label}</option>
          ))}
        </select>
      </div>
    </div>
  </div>
)}
      {/* Tableau des transferts */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Recent Transactions</h3>
              <p className="text-gray-600 mt-1 text-sm">
                Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalTransfers)} of {totalTransfers} transactions
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                  showFilters || Object.values(filters).filter(v => v !== 'all').length > 0
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-3.5 w-3.5" />
                <span>Filters</span>
                {Object.values(filters).filter(v => v !== 'all').length > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[10px] font-semibold rounded-full bg-emerald-600 text-white">
                    {Object.values(filters).filter(v => v !== 'all').length}
                  </span>
                )}
              </button>

              <button
                onClick={() => loadTransferHistory(currentPage)}
                disabled={loading}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="text-xs">Sort by:</span>
                <select
                  value={sortConfig.key}
                  onChange={(e) => handleSort(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-transparent text-xs"
                >
                  <option value="created_at">Date</option>
                  <option value="amount">Amount</option>
                  <option value="status">Status</option>
                </select>
                <button
                  onClick={() => setSortConfig(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))}
                  className="p-1.5 hover:bg-gray-100 rounded-lg"
                >
                  {sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Reference</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">From</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">To</th>
                <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider w-12"></th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10">
                    <LoadingScreen fullscreen={false} message="Loading transactions..." />
                  </td>
                </tr>
              ) : transfers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12">
                    <div className="text-center">
                      <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">No transactions found</h3>
                      <p className="text-gray-500 text-xs max-w-md mx-auto">
                        {searchTerm || Object.values(filters).some(v => v !== 'all')
                          ? 'Try adjusting your search or filters'
                          : 'No transactions have been recorded yet'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                transfers.map((transfer) => {
                  const typeConfig = getTypeConfig(transfer.transfer_type || '');
                  const statusConfig = getStatusColor(transfer.status);

                  return (
                    <tr
                      key={transfer.id || transfer.reference}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className="font-mono text-xs text-gray-900">{transfer.reference || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-600">
                        {formatDate(transfer.created_at)}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className="text-xs text-gray-700">{typeConfig.label}</span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-700">
                        {(() => {
                          const wallets = transfer.source_wallets || [];
                          const requested = (transfer.source_method || '').toLowerCase();
                          const differs = wallets.length > 0 && !(wallets.length === 1 && wallets[0].toLowerCase() === requested);
                          if (!differs) return transfer.source_method || 'N/A';
                          const summary = wallets.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(', ');
                          const tooltip = (transfer.debit_plan || []).length > 0
                            ? (transfer.debit_plan || []).map(p => `${p.method}: ${p.amount.toLocaleString('fr-CD')} ${transfer.currency || ''}`).join('\n')
                            : `Débité depuis ${summary}`;
                          return (
                            <div className="flex flex-col gap-0.5" title={tooltip}>
                              <span>{transfer.source_method}</span>
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded px-1 py-0 w-fit">
                                → {summary}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-700">
                        {transfer.target_method || 'N/A'}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                          {formatCurrency(transfer.amount || 0, transfer.currency || 'USD')}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}>
                          {statusConfig.icon}
                          {formatStatus(transfer.status)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-right">
                        <button
                          onClick={() => loadTransferDetails(transfer.reference || '')}
                          disabled={loadingDetails}
                          className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors disabled:opacity-50"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {transfers.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="text-xs text-gray-600">
                Page {currentPage} of {totalPages} • {totalTransfers.toLocaleString()} total records
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadTransferHistory(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <ChevronUp className="h-3 w-3 rotate-90" />
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => loadTransferHistory(pageNum)}
                        className={`w-8 h-8 flex items-center justify-center text-xs font-medium rounded-lg transition-all ${
                          currentPage === pageNum
                            ? 'bg-emerald-600 text-white shadow-lg'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => loadTransferHistory(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  Next
                  <ChevronDown className="h-3 w-3 rotate-90" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer info */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-500">
          Data is updated in real-time • Last refresh: {new Date().toLocaleTimeString()}
        </p>
      </div>

      {/* Modal des détails */}
      {showDetailsModal && selectedTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* En-tête du modal */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <Info className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Transaction Details</h3>
                  <p className="text-sm text-gray-600">Reference: {selectedTransfer.reference || 'N/A'}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Contenu du modal */}
            <div className="overflow-y-auto max-h-[70vh]">
              <div className="p-6">
                {loadingDetails ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Colonne gauche - Informations principales */}
                    <div className="space-y-6">
                      {/* Informations de base */}
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          Basic Information
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Transaction ID:</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-gray-900 text-sm">{selectedTransfer.id}</span>
                              <button
                                onClick={() => copyToClipboard(selectedTransfer.id.toString())}
                                className="p-1 text-gray-400 hover:text-emerald-600"
                                title="Copy ID"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Reference:</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-gray-900 text-sm">{selectedTransfer.reference || 'N/A'}</span>
                              <button
                                onClick={() => copyToClipboard(selectedTransfer.reference || '')}
                                className="p-1 text-gray-400 hover:text-emerald-600"
                                title="Copy Reference"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Date & Time:</span>
                            <span className="font-medium text-gray-900 text-sm">{formatDate(selectedTransfer.created_at)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Informations financières */}
                      <div className="bg-emerald-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <BanknoteIcon className="h-4 w-4" />
                          Financial Details
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Amount:</span>
                            <span className="text-xl font-bold text-emerald-700">
                              {formatCurrency(selectedTransfer.amount || 0, selectedTransfer.currency || 'USD')}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Currency:</span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800`}>
                              {selectedTransfer.currency}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Type:</span>
                            <span className="font-medium text-gray-900 text-sm">
                              {getTypeConfig(selectedTransfer.transfer_type || '').label}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Informations du marchand */}
                      <div className="bg-blue-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Building className="h-4 w-4" />
                          Merchant Information
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Merchant Code:</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-gray-900 text-sm">{selectedTransfer.merchant_code || 'N/A'}</span>
                              <button
                                onClick={() => copyToClipboard(selectedTransfer.merchant_code || '')}
                                className="p-1 text-gray-400 hover:text-emerald-600"
                                title="Copy Merchant Code"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Method:</span>
                            <span className="font-medium text-gray-900 text-sm">
                              {getMethodConfig(selectedTransfer.method).label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Colonne droite - Informations détaillées */}
                    <div className="space-y-6">
                      {/* Informations des parties */}
                      <div className="bg-purple-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Parties Involved
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">From Account:</div>
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 text-sm">{selectedTransfer.source_method || 'N/A'}</span>
                              <button
                                onClick={() => copyToClipboard(selectedTransfer.source_method || '')}
                                className="p-1 text-gray-400 hover:text-purple-600"
                                title="Copy From"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                            {selectedTransfer.initiated_by && (
                              <div className="text-xs text-gray-500 mt-1">Initiated by: {selectedTransfer.initiated_by}</div>
                            )}
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">To Account:</div>
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 text-sm">{selectedTransfer.target_method || 'N/A'}</span>
                              <button
                                onClick={() => copyToClipboard(selectedTransfer.target_method || '')}
                                className="p-1 text-gray-400 hover:text-purple-600"
                                title="Copy To"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                            {selectedTransfer.approved_by && (
                              <div className="text-xs text-gray-500 mt-1">Processed by: {selectedTransfer.approved_by}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Informations des opérateurs */}
                      <div className="bg-orange-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <RefreshCw className="h-4 w-4" />
                          Operator Details
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Source Operator:</span>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: getOperatorConfig(selectedTransfer.source_method).color }}
                              />
                              <span className="font-medium text-gray-900 text-sm">
                                {getOperatorConfig(selectedTransfer.source_method).label}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Target Operator:</span>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: getOperatorConfig(selectedTransfer.target_method).color }}
                              />
                              <span className="font-medium text-gray-900 text-sm">
                                {getOperatorConfig(selectedTransfer.target_method).label}
                              </span>
                            </div>
                          </div>
                          {(() => {
                            // When biggest-first debits from a wallet other
                            // than the requested source, show it explicitly.
                            // Without this, merchants see e.g. "Airtel cashout"
                            // and their Afrimoney wallet drops with no visible
                            // reason (Big Construct ticket 2026-07-14).
                            const wallets = selectedTransfer.source_wallets || [];
                            const requested = (selectedTransfer.source_method || '').toLowerCase();
                            const differs = wallets.length > 0 && !(wallets.length === 1 && wallets[0].toLowerCase() === requested);
                            if (!differs) return null;
                            const plan = selectedTransfer.debit_plan || [];
                            return (
                              <div className="mt-3 pt-3 border-t border-amber-200">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <span className="text-sm font-semibold text-amber-800">Wallet(s) réellement débité(s)</span>
                                    <p className="text-[11px] text-amber-700 mt-0.5">Le wallet demandé ne suffisait pas — le montant a été prélevé sur le(s) wallet(s) ci-dessous (stratégie biggest-first).</p>
                                  </div>
                                </div>
                                <div className="mt-2 space-y-1.5">
                                  {(plan.length > 0 ? plan : wallets.map(w => ({ method: w, amount: 0 }))).map((p, i) => (
                                    <div key={i} className="flex justify-between items-center text-sm bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                                      <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getOperatorConfig(p.method).color }} />
                                        <span className="font-medium text-gray-900">{getOperatorConfig(p.method).label}</span>
                                      </div>
                                      {p.amount > 0 && (
                                        <span className="tabular-nums text-gray-800">
                                          {new Intl.NumberFormat('fr-CD').format(p.amount)} {selectedTransfer.currency}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Statut et commentaires */}
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Status & Comments
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Status:</span>
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${getStatusColor(selectedTransfer.status).bg} ${getStatusColor(selectedTransfer.status).border}`}>
                              {getStatusColor(selectedTransfer.status).icon}
                              <span className={`text-xs font-medium ${getStatusColor(selectedTransfer.status).text}`}>
                                {formatStatus(selectedTransfer.status)}
                              </span>
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Comments:</div>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm text-gray-700">
                                {selectedTransfer.rejected_reason || 'No comments provided'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Métadonnées */}
                      <div className="bg-gray-100 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Metadata</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-gray-600">Created:</div>
                          <div className="text-gray-900 text-right">{formatDate(selectedTransfer.created_at || '')}</div>
                          <div className="text-gray-600">Updated:</div>
                          <div className="text-gray-900 text-right">{formatDate(selectedTransfer.updated_at || '')}</div>
                          <div className="text-gray-600">Bank Account ID:</div>
                          <div className="text-gray-900 text-right">{selectedTransfer.merchant_type || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Pied du modal */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => window.open(`/transfer/${selectedTransfer.reference}`, '_blank')}
                  className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Full View
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles globaux additionnels */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
        
        .appearance-none {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          background-size: 1em;
        }
        
        select:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
        }
        
        .group:hover .group-hover\\:animate-bounce {
          animation: bounce 1s infinite;
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }

        /* Scrollbar styling */
        .overflow-x-auto::-webkit-scrollbar {
          height: 8px;
        }

        .overflow-x-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .overflow-y-auto::-webkit-scrollbar {
          width: 8px;
        }

        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
};

export default TransferHistory;